// renderer/DynamicForm.tsx
import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Box, Stack, SimpleGrid, Center, Text, Button, Group, Divider } from '@mantine/core';
import { BuilderField, FieldOption } from '../types/BuilderField';
import { groupFields } from '../utils/groupFields';
import { columnSpan } from '../utils/fieldWidth';
import { DynamicField } from './DynamicField';
import { validateFieldValues } from '../utils/fieldValueValidation';

export interface DynamicFormProps {
  fields: BuilderField[];
  values?: Record<string, any>;
  onChange?: (key: string, value: any) => void;
  onSubmit?: (values: Record<string, any>) => void;
  submitLabel?: string;
  hideSubmit?: boolean;
  /** Choices for any 'member' fields in the form. */
  memberOptions?: FieldOption[];
}

/**
 * Imperative handle exposed via ref, so any parent (wizard step, plain page,
 * whatever) can trigger validation before advancing/submitting without this
 * component needing to know anything about that parent's structure.
 */
export interface DynamicFormHandle {
  /** Runs required + format validation, reveals errors on the fields that
   *  fail, and returns whether the form is currently valid. */
  validate: () => boolean;
  /** Clears any currently-shown validation errors without touching values. */
  clearErrors: () => void;
}

/**
 * Public, project-agnostic runtime form. Renders whatever schema you give it
 * (typically the same BuilderField[] that came out of FormBuilder / your
 * adapter's loadFields()). No knowledge of any specific project's domain.
 *
 * Validation (required + email/phone format) lives here, not in the pages
 * that use it, so every form built on this engine gets it for free:
 *
 *   const formRef = useRef<DynamicFormHandle>(null);
 *   <DynamicForm ref={formRef} fields={fields} values={values} onChange={setValues} hideSubmit />
 *   ...
 *   if (!formRef.current?.validate()) return; // blocks advancing on invalid input
 */
export const DynamicForm = forwardRef<DynamicFormHandle, DynamicFormProps>(function DynamicForm(
  { fields, values = {}, onChange, onSubmit, submitLabel = 'Submit', hideSubmit, memberOptions },
  ref
) {
  const activeFields = useMemo(() => fields.filter((f) => f.active), [fields]);
  const sections = useMemo(() => groupFields(activeFields), [activeFields]);

  // Only fieldKeys present here show an error. Kept internal so pages using
  // this form never have to compute or pass down error state themselves.
  const [errors, setErrors] = useState<Record<string, string>>({});

  useImperativeHandle(ref, () => ({
    validate: () => {
      const nextErrors = validateFieldValues(activeFields, values);
      setErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    },
    clearErrors: () => setErrors({}),
  }), [activeFields, values]);

  const handleChange = (key: string, value: any) => {
    onChange?.(key, value);
    // Clear that field's error the moment the user edits it, so the red
    // state doesn't linger after they've fixed it.
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = () => {
    const nextErrors = validateFieldValues(activeFields, values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      onSubmit?.(values);
    }
  };

  if (sections.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">This form has no active fields yet.</Text>
      </Center>
    );
  }

  return (
    <Stack gap="xl">
      {sections.map((section) => (
        <Box key={section.category}>
          <Divider mb="sm" label={section.category} labelPosition="center" orientation="horizontal" />
          <Stack gap="sm">
            {section.rows.map((row) => (
              <SimpleGrid key={row.orderNo} cols={12} spacing="sm">
                {row.fields.map((field) => (
                  <Box
                    key={field.id}
                    style={{ gridColumn: `span ${field.fieldType === 'url' ? 12 : columnSpan(row.fields.length)}` }}
                  >
                    <DynamicField
                      field={field}
                      value={values[field.fieldKey]}
                      onChange={(v) => handleChange(field.fieldKey, v)}
                      error={errors[field.fieldKey]}
                      memberOptions={memberOptions}
                    />
                  </Box>
                ))}
              </SimpleGrid>
            ))}
          </Stack>
        </Box>
      ))}

      {!hideSubmit && onSubmit && (
        <Group justify="flex-end">
          <Button onClick={handleSubmit}>{submitLabel}</Button>
        </Group>
      )}
    </Stack>
  );
});